import * as Yup from 'yup';

import { QUOTE_VALIDATION_UNAVAILABLE_FIELD } from 'core/components/validation-errors-modal/validation-errors-modal.constants';

/**
 * Form validation for the Staff Quote Revisit modules (#36353), composed in one place so lanes add
 * rule files instead of editing the shared offer schema.
 *
 * A rule file is any `*.validation-rules.js` under src/quote-revisit/, kept next to the module it
 * validates. Its default export takes the same arguments as the offer form's `buildSchema` and
 * returns a Yup object schema, or `null` when it has nothing to add for that call.
 *
 * Lane schemas are validated on their own, next to the offer schema, never merged into it. Yup's
 * `concat` replaces an overlapping field's rules, which would drop the offer schema's `.when()`
 * requirements on fields a lane also validates (payment methods, mortgage details).
 *
 * Rule files are imported lazily, on the first validation with the switch on, so brands with the
 * switch off never load them. `index.test.js` checks every rule file's shape in CI.
 */

// Thrown when the lane rules can't run, so the quote can't be fully validated. The offer form turns
// it into a blocking error (`formErrors`) rather than letting Update quote or Continue to checkout
// through unchecked.
export class QuoteRevisitValidationError extends Error {
  constructor({ message, formMessage, cause }) {
    super(message, { cause });
    this.name = 'QuoteRevisitValidationError';
    this.formErrors = { [QUOTE_VALIDATION_UNAVAILABLE_FIELD]: formMessage };
  }
}

// The rule files couldn't be loaded, e.g. a chunk that failed to fetch after a deploy.
export class QuoteRevisitValidationUnavailableError extends QuoteRevisitValidationError {
  constructor(cause) {
    super({
      message: 'Quote Revisit validation rules could not be loaded',
      formMessage: "We couldn't load everything needed to check this quote. Reload the page and try again.",
      cause
    });
    this.name = 'QuoteRevisitValidationUnavailableError';
  }
}

// A lane rule loaded but can't be trusted: it threw, returned something other than a Yup schema, or
// reported an error the form would drop (no field path or no message). A code bug that a reload
// won't fix.
export class QuoteRevisitRuleError extends QuoteRevisitValidationError {
  constructor(cause) {
    super({
      message: 'A Quote Revisit validation rule failed to run correctly',
      formMessage:
        'Something went wrong while checking this quote. Try again, and contact support if it keeps happening.',
      cause
    });
    this.name = 'QuoteRevisitRuleError';
  }
}

const buildRuleSchema = (rule, buildSchemaArgs) => {
  let ruleSchema;
  try {
    ruleSchema = rule(buildSchemaArgs);
  } catch (error) {
    throw new QuoteRevisitRuleError(error);
  }
  if (ruleSchema && !Yup.isSchema(ruleSchema)) {
    throw new QuoteRevisitRuleError(new TypeError('A Quote Revisit validation rule must return a Yup schema or null'));
  }
  return ruleSchema;
};

export const loadValidationRules = (ruleModules) =>
  Object.entries(ruleModules).map(([path, ruleModule]) => {
    if (typeof ruleModule?.default !== 'function') {
      throw new TypeError(`${path} must default-export a function (buildSchemaArgs) => YupSchema | null`);
    }
    return ruleModule.default;
  });

// A failed load isn't cached, so the next validation retries it (e.g. after a chunk that failed to
// fetch becomes available again). A load that resolves with no module counts as failed: the stale
// chunk reload handler can resolve a failed preload as undefined while it reloads the page.
export const createRuleLoader = (ruleModuleLoaders) => {
  let loadedRules;
  return async () => {
    loadedRules ??= Promise.all(
      Object.entries(ruleModuleLoaders).map(async ([path, loadRuleModule]) => [path, await loadRuleModule()])
    ).then((ruleModules) => loadValidationRules(Object.fromEntries(ruleModules)));
    try {
      return await loadedRules;
    } catch (error) {
      loadedRules = undefined;
      throw new QuoteRevisitValidationUnavailableError(error);
    }
  };
};

const loadQuoteRevisitValidationRules = createRuleLoader(import.meta.glob('../**/*.validation-rules.js'));

export const composeValidationRules = (schema, buildSchemaArgs, rules) => {
  const ruleSchemas = rules.map((rule) => buildRuleSchema(rule, buildSchemaArgs)).filter(Boolean);
  if (ruleSchemas.length === 0) {
    return schema;
  }

  return schema.test({
    name: 'quoteRevisitRules',
    async test(value) {
      const results = await Promise.allSettled(
        // async so a synchronous throw (yup resolves a root .when() before creating its promise)
        // becomes a rejection that allSettled collects, rather than escaping unwrapped.
        ruleSchemas.map(async (ruleSchema) =>
          ruleSchema.validate(value, { context: this.options.context, abortEarly: false })
        )
      );
      const failures = results.filter(({ status }) => status === 'rejected').map(({ reason }) => reason);
      const unexpected = failures.find((reason) => !Yup.ValidationError.isError(reason));
      if (unexpected) {
        throw new QuoteRevisitRuleError(unexpected);
      }
      // Formik's yupToFormErrors drops an error with no path, and setIn drops one whose message is
      // undefined, so either would let the form submit unchecked.
      const laneErrors = failures.flatMap((failure) => (failure.inner.length ? failure.inner : [failure]));
      const droppedErrors = laneErrors.filter(({ path, message }) => !path || message == null);
      if (droppedErrors.length > 0) {
        const described = droppedErrors
          .map(({ path, message, type }) => `${path || '(no path)'}: ${message ?? `(no message, test "${type}")`}`)
          .join('; ');
        throw new QuoteRevisitRuleError(
          new TypeError(
            `A Quote Revisit validation rule reported an error the form would drop. Give every lane error a field path (this.createError({ path })) and a message. Dropped: ${described}`
          )
        );
      }
      return failures.length === 0 || new Yup.ValidationError(failures, value);
    }
  });
};

export const applyQuoteRevisitValidationRules = async (
  schema,
  buildSchemaArgs,
  loadRules = loadQuoteRevisitValidationRules
) => composeValidationRules(schema, buildSchemaArgs, await loadRules());
